import {
  Badge,
  Box,
  Button,
  Flex,
  HStack,
  IconButton,
  Input,
  Popover,
  PopoverArrow,
  PopoverBody,
  PopoverContent,
  PopoverFooter,
  PopoverTrigger,
  Td,
  Text,
  Tooltip,
  Tr,
  VisuallyHidden,
  Wrap,
  WrapItem,
} from "@chakra-ui/react";
import { ArrowCounterClockwise, ArrowRight, CaretDown, Check, Equals, Info, PencilSimple, Trash, X } from "@phosphor-icons/react";
import { REVIEW_STATE } from "../../../../utils/foundationReviewCompare";
import { useEffect, useState } from "react";
import { getFormattedNumValue } from "../../../../utils";
import { familyFor } from "./unitFamilies";
import { ACTIONS_SLOT_WIDTH, limeFocus, splitPerUnit, UNIT_SEGMENT_WIDTH, VALUE_FIELD_WIDTH } from "./fieldStyles";

const CONFIDENCE_COLORS = { high: "green", medium: "yellow", low: "red" };

// A faint warm tint for rows awaiting approval, the orange counterpart of the data mapping tool's "#FAFDF5".
const PENDING_ROW_BG = "#FFFAF3";

/* Row marks: orange = this changes (pending approval, or a review value that replaces the current one) — tint
   + left bar; green = review value that matches the current one — left bar only, since nothing changes. */
const ROW_MARK = {
  orange: { bg: PENDING_ROW_BG, hover: "orange.50", bar: "orange.400" },
  green: { bg: undefined, hover: "gray.50", bar: "green.500" },
};


/**
 * WHERE IN a document a row came from, in whichever citation shape applies: PDFs cite a page, workbooks
 * cite a sheet and sometimes a cell. Null when neither is known — the row is still shown, not hidden.
 */
const locationLabel = (row) => {
  if (row.sourcePage != null) return `p. ${row.sourcePage}`;
  if (row.sourceSheet) return row.sourceCell ? `${row.sourceSheet}!${row.sourceCell}` : row.sourceSheet;
  return null;
};

/**
 * Which document a row came from, plus where in it.
 *
 * The filename matters as soon as a year has more than one source: two documents reporting the same metric
 * for different sites are both legitimate, and without the filename they are indistinguishable in the
 * table. Rows extracted before this was recorded have no filename and fall back to the location alone.
 *
 * Exported: `MetricInfoOverlay` (the compact (i) icon's destination, metrics-table-2a) reuses this exact
 * helper for its "Source" field, rather than re-deriving the same file-name/page/sheet logic a second time.
 */
export const sourceLabel = (row) => {
  const where = locationLabel(row);
  if (row.sourceFileName && where) return `${row.sourceFileName} · ${where}`;
  return row.sourceFileName || where || "—";
};

/**
 * Flattens one metadata entry to `key: value` for display.
 *
 * The keys are chosen by the agent, so nothing here may assume a shape. A nested object or array is
 * stringified rather than dropped — showing raw JSON is worse than nothing only if it is never useful, and
 * the alternative is silently hiding something the model thought worth recording.
 *
 * Exported for the same reason as `sourceLabel` above.
 */
export const metadataEntries = (metadata) => {
  if (!metadata || typeof metadata !== "object") return [];
  return Object.entries(metadata)
    .filter(([, value]) => value !== null && value !== undefined && value !== "")
    .map(([key, value]) => ({
      key,
      text: `${key}: ${typeof value === "object" ? JSON.stringify(value) : String(value)}`,
    }));
};

const EDITABLE_FIELDS = ["category", "dataKey", "dataValue", "unit", "scope"];
// Compact mode's edit row only exposes these two — see the design brief this followed (metrics-table-2a):
// the datapoint is already named by the structure, so retyping it, its category, or "applies to" has no
// natural place in a Value/Unit-only field. Those stay editable in FULL mode (ExtractionReviewModal, the
// no-structure fallback), where EDITABLE_FIELDS above is still used in full.
const COMPACT_EDITABLE_FIELDS = ["dataValue", "unit"];

const draftFrom = (row) => EDITABLE_FIELDS.reduce((acc, field) => ({ ...acc, [field]: row[field] ?? "" }), {});

/**
 * One metric row: read mode, or an inline editor with an EXPLICIT save.
 *
 * Deliberately not a debounced autosave. These are numeric fields whose parsed value is recomputed
 * server-side on every write, and a debounce there is where lost-edit bugs live — a keystroke landing
 * after a refetch silently reverts, and the user cannot tell whether their number was stored.
 *
 * Only the fields a human would correct are editable. `valueNumeric` is absent on purpose: it is derived
 * from `dataValue` by the server, so offering it would let the number and the text it came from disagree.
 *
 * `compact` switches between the full 8-column read row (Metric/Value/Unit/Applies to/Source/Reference/
 * Details/Confidence — used by `ExtractionReviewModal` and the no-structure fallback, where nothing is
 * "required" yet so every column is worth seeing) and the 3-column read row (Datapoint/Value/Actions —
 * used by the main Foundation Data page whenever a target structure exists). In compact mode Value and
 * Unit read as ONE bordered field (metrics-table-2a design pass) rather than separate columns, and
 * Applies to/Source/Reference/Details/Confidence move into `MetricInfoOverlay` (opened via `onShowUsage`,
 * the same prop that also opens the usage trace there — one (i) icon now covers both).
 *
 * Compact edit mode only exposes Value + Unit (see `COMPACT_EDITABLE_FIELDS`) — the datapoint name,
 * category and "applies to" stay read-only here; full mode keeps all of them editable, laid
 * out across its 8 real columns, exactly as before.
 *
 * In compact mode the "delete" action (now reachable only from inside edit mode, next to Save/Cancel)
 * CLEARS the value (`dataValue: ""`, via the normal save path) rather than removing the row — a figure
 * sitting in the applied-data-points table is a datapoint the structure asked for, and the ask was to
 * blank its answer, not to delete the datapoint. Full mode keeps today's hard delete (`onDelete`), since
 * that is the shape `ExtractionReviewModal` and the no-structure fallback need.
 */
export default function EditableMetricRow({
  row,
  canEdit,
  onSave,
  onDelete,
  isSaving,
  isDeleting,
  usage,
  onShowUsage,
  compact = false,
  /* Review overlay only (compareWithCurrent): the workstream's current figure for this datapoint, and how
     it relates to this document's — so a reviewer can compare before accepting. */
  comparison = null,
  onEditingChange,
  // Review overlay: this row is crossed out for this ingest, and the toggle (absent when read-only).
  isIgnored = false,
  onToggleIgnored,
}) {
  const [draft, setDraft] = useState(null);
  const isEditing = draft !== null;
  const editorId = `row-${row.id}`;

  // Tells the page an editor is open, so Submit/Approve stay disabled until it is saved or cancelled.
  useEffect(() => {
    if (!onEditingChange) return undefined;
    onEditingChange(editorId, isEditing);
    return () => onEditingChange(editorId, false);
  }, [onEditingChange, editorId, isEditing]);

  const startEdit = () => setDraft(draftFrom(row));
  const cancel = () => setDraft(null);

  // Compact mode's edit row only ever changes dataValue/unit (see COMPACT_EDITABLE_FIELDS).
  const saveCompact = () => {
    const changed = COMPACT_EDITABLE_FIELDS.reduce((acc, field) => {
      if ((row[field] ?? "") !== draft[field]) acc[field] = draft[field];
      return acc;
    }, {});
    if (Object.keys(changed).length === 0) {
      setDraft(null);
      return;
    }
    onSave({ id: row.id, data: changed }, () => setDraft(null));
  };

  const save = () => {
    // Send only what actually changed. A null field means "leave unchanged" server-side, so sending the
    // whole row would make an untouched blank field indistinguishable from a deliberate clear.
    const changed = EDITABLE_FIELDS.reduce((acc, field) => {
      if ((row[field] ?? "") !== draft[field]) acc[field] = draft[field];
      return acc;
    }, {});
    if (Object.keys(changed).length === 0) {
      setDraft(null);
      return;
    }
    onSave({ id: row.id, data: changed }, () => setDraft(null));
  };

  const set = (field) => (event) => setDraft((prev) => ({ ...prev, [field]: event.target.value }));

  const clearValue = (onClose) => {
    onClose();
    onSave({ id: row.id, data: { dataValue: "" } });
  };

  // In compact mode this CLEARS the value (`dataValue: ""`, via the normal save path) rather than removing
  // the row — a figure sitting in the applied-data-points table is a datapoint the structure asked for, and
  // the ask was to blank its answer, not to delete the datapoint. Full mode keeps the hard delete
  // (`onDelete`), since that is the shape `ExtractionReviewModal` and the no-structure fallback need. Only
  // ever rendered from inside edit mode now (metrics-table-2a) — the read row shows just info + pencil.
  const deleteConfirm = compact ? (
    <Popover placement="left">
      {({ onClose }) => (
        <>
          <PopoverTrigger>
            <IconButton
              size="xs"
              variant="ghost"
              aria-label={`Clear the value for ${row.dataKey}`}
              icon={<Trash size={14} />}
              isLoading={isSaving}
            />
          </PopoverTrigger>
          <PopoverContent w="240px">
            <PopoverArrow />
            <PopoverBody fontSize="sm">
              Clear the value for “{row.dataKey}”? This resets it to not reported.
            </PopoverBody>
            <PopoverFooter display="flex" justifyContent="flex-end" gap={2}>
              <Button size="xs" variant="secondary" onClick={onClose}>
                Cancel
              </Button>
              <Button size="xs" colorScheme="orange" onClick={() => clearValue(onClose)}>
                Clear
              </Button>
            </PopoverFooter>
          </PopoverContent>
        </>
      )}
    </Popover>
  ) : (
    // Confirmed, because deleting is not obviously reversible from the UI even though the server
    // soft-deletes.
    <Popover placement="left">
      {({ onClose }) => (
        <>
          <PopoverTrigger>
            <IconButton
              size="xs"
              variant="ghost"
              aria-label={`Delete ${row.dataKey}`}
              icon={<Trash size={14} />}
              isLoading={isDeleting}
            />
          </PopoverTrigger>
          <PopoverContent w="220px">
            <PopoverArrow />
            <PopoverBody fontSize="sm">Remove “{row.dataKey}” from this year?</PopoverBody>
            <PopoverFooter display="flex" justifyContent="flex-end" gap={2}>
              <Button size="xs" variant="secondary" onClick={onClose}>
                Cancel
              </Button>
              <Button
                size="xs"
                colorScheme="red"
                onClick={() => {
                  onClose();
                  onDelete(row.id);
                }}
              >
                Remove
              </Button>
            </PopoverFooter>
          </PopoverContent>
        </>
      )}
    </Popover>
  );

  if (isEditing && compact) {
    // Compact edit mode: only the value and unit are live, laid out as one field matching read mode's
    // shape — green active border + focus ring instead of the field's resting grey border. The unit side
    // becomes a Select only when the row's unit is a known, relabel-only family (unitFamilies.js);
    // otherwise it stays the same fixed suffix read mode shows (e.g. "%", which has no alternatives).
    const unitOptions = familyFor(row.unit);
    const editUnit = splitPerUnit(row.unit);
    return (
      <Tr bg="limeShade.50">
        <Td>
          <Text>
            {row.dataKey}
            {editUnit.per ? (
              <Text as="span" ml={2} fontSize="12px" color="gray.600">
                {editUnit.per}
              </Text>
            ) : null}
          </Text>
        </Td>
        <Td>
          <Flex justify="flex-end">
            <Flex
              border="1.5px solid"
              borderColor="lime"
              borderRadius="8px"
              overflow="hidden"
              bg="white"
              boxShadow="0 0 0 3px rgba(133,194,42,.18)"
              flexShrink={0}
              w={VALUE_FIELD_WIDTH}
            >
              <Input
                autoFocus
                variant="unstyled"
                flex="1"
                minW={0}
                px="10px"
                py="7px"
                textAlign="right"
                fontSize="14px"
                fontWeight="600"
                sx={{ fontVariantNumeric: "tabular-nums" }}
                value={draft.dataValue}
                onChange={set("dataValue")}
                onKeyDown={(e) => {
                  if (e.key === "Enter") saveCompact();
                  if (e.key === "Escape") cancel();
                }}
              />
              {unitOptions ? (
                // A plain native <select> (via Chakra's `as`), not Chakra's own `Select` — that component
                // always reserves its own built-in padding/icon slot even with variant="unstyled", which
                // inside this narrow 84px segment collided with the unit text and visually ate a
                // character (e.g. "tCO2e" rendering as "tCO2" under the chevron). `appearance="none"`
                // strips the native arrow entirely and CaretDown below is positioned by hand instead.
                <Box position="relative" w={UNIT_SEGMENT_WIDTH} flexShrink={0}>
                  <Box
                    as="select"
                    w="100%"
                    h="100%"
                    appearance="none"
                    border="none"
                    outline="none"
                    fontSize="12.5px"
                    fontWeight="600"
                    fontFamily="inherit"
                    color="gray.600"
                    bg="gray.50"
                    borderLeft="1px solid"
                    borderColor="gray.200"
                    pl={2}
                    pr="20px"
                    value={draft.unit}
                    onChange={set("unit")}
                  >
                    {unitOptions.map((option) => (
                      <option key={option} value={option}>
                        {option}
                      </option>
                    ))}
                  </Box>
                  <Box position="absolute" right="7px" top="50%" transform="translateY(-50%)" pointerEvents="none">
                    <CaretDown size={10} color="#718096" />
                  </Box>
                </Box>
              ) : (
                <Flex
                  w={UNIT_SEGMENT_WIDTH}
                  flexShrink={0}
                  align="center"
                  justify="center"
                  fontSize="12px"
                  color="gray.600"
                  bg="gray.50"
                  borderLeft="1px solid"
                  borderColor="gray.200"
                >
                  {editUnit.base || "—"}
                </Flex>
              )}
            </Flex>
          </Flex>
        </Td>
        {/* Its own column (not packed into the Value cell via colSpan) — see ACTIONS_SLOT_WIDTH's comment
            in fieldStyles.js for why a real, separate table column is what keeps the Value field's position
            fixed regardless of how many icons a given row actually shows. */}
        <Td w={ACTIONS_SLOT_WIDTH}>
          <Flex justify="flex-end" align="center" gap={2}>
            <IconButton
              size="xs"
              bg="lime"
              color="darkTeal"
              _hover={{ bg: "limeShade.600" }}
              aria-label="Save"
              icon={<Check size={14} weight="bold" />}
              isLoading={isSaving}
              onClick={saveCompact}
            />
            <IconButton
              size="xs"
              variant="secondary"
              aria-label="Cancel"
              icon={<X size={12} />}
              onClick={cancel}
            />
            {/* Delete/clear moved here from the read row (metrics-table-2a) — the mockup's read row shows
                only info + pencil, so this third action is reachable one step later, inside edit mode,
                instead of sitting beside the read row's icons. Branching unchanged from before: a row
                matched to a target datapoint clears its value (back to a gap); an off-schema row (no
                `target`) is still hard-deleted, since there is no gap for it to revert to. */}
            {deleteConfirm}
          </Flex>
        </Td>
      </Tr>
    );
  }

  if (isEditing) {
    return (
      <Tr bg="limeShade.50">
        <Td colSpan={8}>
          <Flex wrap="wrap" gap={2} align="center">
            <Input size="xs" w="12rem" placeholder="Metric" value={draft.dataKey} onChange={set("dataKey")} {...limeFocus} />
            <Input size="xs" w="10rem" placeholder="Category" value={draft.category} onChange={set("category")} {...limeFocus} />
            <Input size="xs" w="7rem" placeholder="Value" value={draft.dataValue} onChange={set("dataValue")} {...limeFocus} />
            <Input size="xs" w="6rem" placeholder="Unit" value={draft.unit} onChange={set("unit")} {...limeFocus} />
            <Input size="xs" w="8rem" placeholder="Applies to" value={draft.scope} onChange={set("scope")} {...limeFocus} />
            <HStack spacing={1}>
              <Button size="xs" variant="primary" leftIcon={<Check size={12} />} isLoading={isSaving} onClick={save}>
                Save
              </Button>
              <IconButton size="xs" variant="secondary" aria-label="Cancel" icon={<X size={12} />} onClick={cancel} />
            </HStack>
          </Flex>
        </Td>
      </Tr>
    );
  }

  // "tCO2e per currency unit": base stays in the unit segment, the "per …" tail sits beside the name.
  const unitParts = splitPerUnit(row.unit);
  const isPending = row.published === false;
  // Review overlay: this document's value differs from the current one, so Accept will change it. Marked
  // like a pending row (orange tint + bar) — the same "this changes" signal the page uses.
  const willReplace = comparison?.state === REVIEW_STATE.DIFFERS && !!comparison.current;
  // Review overlay: this document's value equals the current one — marked in green, the "no change" signal.
  const isMatch = comparison?.state === REVIEW_STATE.MATCHES && !!comparison.current;
  const isMarked = !isIgnored && (isPending || willReplace || isMatch);
  const markTone = isMatch ? "green" : "orange";
  const datapointCell = (
    <Flex direction="column" align="flex-start" minW={0} maxW="100%">
      {/*
        Name + "pending approval" share ONE line — the badge reads as a status on this specific name, not
        as a separate fact stacked underneath it, and it keeps the compact row compact. The Tooltip wraps
        just the name (not the HStack) since only the name itself gets cut off; the badge never does.
      */}
      <HStack spacing={2} minW={0} maxW="100%">
        {/*
          The name IS the way in to "what depends on this figure", and it is not gated on the count: asking
          the question about a figure nothing cites is exactly when the answer reassures, and until now
          there was nothing to click on those rows at all — the modal's own "no answers drafted yet" copy
          was unreachable.

          A Button, not a Text with onClick, so it takes keyboard focus and announces itself. Default text
          colour on purpose: sixty lime links down a table reads as a warning, and the hover underline is
          enough to make it discoverable.

          Guarded on `datapointUid` — a row written before datapoint versioning can carry none, and there is
          nothing to look up for it.

          Kept to one line — a long datapoint name wrapping to three or four lines made every row in the
          group a different height. The Tooltip carries the full name for whatever an ellipsis cuts off.
        */}
        <Tooltip label={row.dataKey} openDelay={400}>
          {row.datapointUid ? (
            <Button
              variant="link"
              fontWeight="500"
              fontSize="inherit"
              color="inherit"
              textAlign="left"
              minW={0}
              maxW="100%"
              isTruncated
              display="block"
              _hover={{ textDecoration: "underline" }}
              aria-label={`What uses ${row.dataKey}`}
              onClick={() => onShowUsage?.(row)}
            >
              {row.dataKey}
            </Button>
          ) : (
            <Text minW={0} maxW="100%" isTruncated fontWeight="500">
              {row.dataKey}
            </Text>
          )}
        </Tooltip>
        {compact && unitParts.per ? (
          <Text flexShrink={0} fontSize="12px" color="gray.600" whiteSpace="nowrap">
            {unitParts.per}
          </Text>
        ) : null}
        {/*
          ESG-4012: a version the owner has not approved yet — what the table shows, not what answers and AI
          Review read. Marked on the whole row (tint + left bar, below) rather than with a tag, matching the
          data mapping tool's "added" rows and the pending-changes banner's own orange bar. `=== false` so a
          row from an endpoint that does not carry the flag is not mislabelled. Screen readers get the text.
        */}
        {isPending ? <VisuallyHidden>Pending approval</VisuallyHidden> : null}
      </HStack>
    </Flex>
  );

  const valueCell = (
    // The verbatim string is always shown. A null valueNumeric means the value has no single number
    // (a range, or "n/a") — such a row is deliberately not chartable, and saying so beats a silent 0.
    <Tooltip
      label={row.valueNumeric == null ? "No single numeric value — not included in charts" : undefined}
      isDisabled={row.valueNumeric != null}
    >
      <Text
        as="span"
        color={row.valueNumeric == null ? "gray.500" : undefined}
        fontStyle={row.valueNumeric == null ? "italic" : undefined}
      >
        {row.dataValue || "—"}
      </Text>
    </Tooltip>
  );

  if (compact) {
    // Read-mode value: plain right-aligned text with the unit beside it, like the Insights tables; the
    // bordered input-style box appears only for an empty value (and in edit mode). Not clickable: editing
    // is reachable only via the pencil below.
    const missing = row.dataValue == null || row.dataValue === "";
    const isNa = String(row.dataValue).trim().toLowerCase() === "n/a";
    const displayValue = missing
      ? "Not reported"
      : row.valueNumeric != null
      ? getFormattedNumValue(row.valueNumeric, 2)
      : row.dataValue;
    return (
      <Tr
        bg={isMarked ? ROW_MARK[markTone].bg : undefined}
        _hover={canEdit ? { bg: isMarked ? ROW_MARK[markTone].hover : "gray.50" } : undefined}
      >
        <Td position="relative">
          {isMarked ? (
            <Tooltip
              label={
                isMatch
                  ? "This document's value matches the current one."
                  : willReplace
                  ? "Accepting this document replaces the current value."
                  : "Not yet approved. Answers and AI Review use the last approved figure until the owner approves this workstream."
              }
              hasArrow
              placement="right"
            >
              <Box position="absolute" left={0} top={0} bottom={0} w="3px" bg={ROW_MARK[markTone].bar} />
            </Tooltip>
          ) : null}
          {datapointCell}
        </Td>
        <Td>
          {comparison ? (
            <ReviewValueCells
              value={displayValue}
              unit={unitParts.base}
              current={willReplace || isMatch ? comparison.current : null}
              tone={markTone}
              onShowDetails={() => onShowUsage?.(comparison.current)}
              isIgnored={isIgnored}
              onToggleIgnored={
                onToggleIgnored && (willReplace || isMatch) ? () => onToggleIgnored(row.id) : undefined
              }
            />
          ) : (
          <Flex justify="flex-end" align="center">
            {/* Boxed only while there is nothing to read (a value cleared back to blank) — a filled figure
                reads as plain text like the Insights tables. Same fixed width and segment split either way,
                so a plain value's last digit lines up with a boxed one's and the unit sits where the box's
                unit segment would. */}
            <Flex
              border="1px solid"
              borderColor={missing ? "gray.200" : "transparent"}
              borderRadius="8px"
              overflow="hidden"
              bg={missing ? "white" : "transparent"}
              flexShrink={0}
              w={VALUE_FIELD_WIDTH}
            >
              <Text
                flex="1"
                minW={0}
                px="10px"
                py="7px"
                textAlign="right"
                fontSize="14px"
                fontWeight={missing || isNa ? "500" : "600"}
                color={missing || isNa ? "gray.400" : "gray.700"}
                sx={{ fontVariantNumeric: "tabular-nums" }}
                noOfLines={1}
              >
                {displayValue}
              </Text>
              <Flex
                w={UNIT_SEGMENT_WIDTH}
                flexShrink={0}
                align="center"
                justify={missing ? "center" : "flex-start"}
                fontSize="12px"
                color="gray.600"
                bg={missing ? "gray.50" : "transparent"}
                borderLeft="1px solid"
                borderColor={missing ? "gray.200" : "transparent"}
                px={missing ? 0 : 1}
              >
                {unitParts.base || (missing ? "—" : "")}
              </Flex>
            </Flex>
          </Flex>
          )}
        </Td>
        {/* No actions column in the review overlay: it is read-only (no pencil), and a differing row's (i) sits
            beside its current value — so the space goes to the values. Everywhere else this is its own fixed
            column; see ACTIONS_SLOT_WIDTH's comment in fieldStyles.js for why. */}
        {comparison ? null : (
        <Td w={ACTIONS_SLOT_WIDTH}>
          <Flex justify="flex-end" align="center" gap={1}>
            {/* Opens MetricInfoOverlay — provenance (Applies to/Source/Reference/Confidence) AND the
                usage trace in one place, since the row shrank to Datapoint/Value and there's no longer
                room for either as its own column or its own icon. Orange — same "worth re-checking"
                signal the usage badge used to carry on its own — only when something in the usage trace
                was drafted from an earlier version of this figure. */}
            <Tooltip
              label={
                usage?.questionsSuperseded > 0
                  ? `${usage.questionsSuperseded} of ${usage.questionsUsing} answers were drafted from an earlier version. See Details.`
                  : undefined
              }
              isDisabled={!(usage?.questionsSuperseded > 0)}
              hasArrow
            >
              <IconButton
                size="xs"
                variant="ghost"
                color={usage?.questionsSuperseded > 0 ? "orange.500" : "gray.500"}
                aria-label={`Details for ${row.dataKey}`}
                icon={<Info size={14} />}
                onClick={() => onShowUsage?.(row)}
              />
            </Tooltip>
            {canEdit ? (
              <IconButton
                size="xs"
                variant="ghost"
                aria-label={`Edit ${row.dataKey}`}
                icon={<PencilSimple size={14} />}
                onClick={startEdit}
              />
            ) : null}
          </Flex>
        </Td>
        )}
      </Tr>
    );
  }

  return (
    <Tr _hover={canEdit ? { bg: "gray.50" } : undefined}>
      <Td>{datapointCell}</Td>
      <Td isNumeric>{valueCell}</Td>
      <Td>{row.unit || "—"}</Td>
      <Td>{row.scope || "—"}</Td>
      <Td maxW="200px">
        {/* The tooltip carries only what the cell had to truncate — the quote has its own column now. */}
        <Tooltip label={sourceLabel(row)} isDisabled={!row.sourceFileName}>
          <Text as="span" color="gray.600" noOfLines={1} wordBreak="break-all">
            {sourceLabel(row)}
          </Text>
        </Tooltip>
      </Td>
      <Td maxW="280px">
        {/* Read-only on purpose: this is the document's own wording, not our data. Letting it be edited
            would allow the citation to disagree with the source it is meant to evidence. */}
        {row.sourceQuote ? (
          <Tooltip label={row.sourceQuote}>
            <Text as="span" color="gray.600" fontStyle="italic" noOfLines={2}>
              “{row.sourceQuote}”
            </Text>
          </Tooltip>
        ) : (
          <Text color="gray.400">—</Text>
        )}
      </Td>
      <Td maxW="200px">
        {metadataEntries(row.metadata).length > 0 ? (
          <Tooltip label={metadataEntries(row.metadata).map((e) => e.text).join(" · ")}>
            <Wrap spacing={1}>
              {metadataEntries(row.metadata).map((entry) => (
                <WrapItem key={entry.key}>
                  <Badge variant="subtle" colorScheme="gray" fontWeight="400" textTransform="none">
                    <Text as="span" noOfLines={1}>
                      {entry.text}
                    </Text>
                  </Badge>
                </WrapItem>
              ))}
            </Wrap>
          </Tooltip>
        ) : (
          <Text color="gray.400">—</Text>
        )}
      </Td>
      <Td>
        <HStack spacing={1} justify="space-between">
          {row.origin === "USER" ? (
            <Badge colorScheme="purple">manual</Badge>
          ) : row.confidence ? (
            <Badge colorScheme={CONFIDENCE_COLORS[row.confidence] || "gray"}>{row.confidence}</Badge>
          ) : (
            <Text color="gray.500">—</Text>
          )}

          <HStack spacing={0}>
            {canEdit ? (
              <>
                <IconButton
                  size="xs"
                  variant="ghost"
                  aria-label={`Edit ${row.dataKey}`}
                  icon={<PencilSimple size={14} />}
                  onClick={startEdit}
                />
                {deleteConfirm}
              </>
            ) : null}
          </HStack>
        </HStack>
      </Td>
    </Tr>
  );
}

/**
 * Review overlay value cell: three fixed columns, read left to right —
 *   [current value + unit] [(i) + sign] [this document's value + unit]
 * The current figure appears only when there is one (orange "→" = accepting replaces it, dark-green "=" = it
 * already matches); a new row leaves the first two columns empty. Never struck through: the current figure is
 * simply what is there now. Every column is a fixed width with numbers right-aligned and tabular, so a 15-digit
 * value cannot run into its neighbour and every row's figures stay in straight columns. The (i) opens the
 * CURRENT figure (history, status, usage).
 */
const CURRENT_TONE = {
  orange: { text: "orange.800", sign: "orange.400", tip: "Current value — accepting replaces it" },
  ignored: { text: "gray.500", sign: "gray.400", tip: "Current value — kept; this row is ignored for this ingest" },
  green: { text: "green.500", sign: "green.500", tip: "Current value — this document's matches it. The value will not change, but accepting updates its source to this document." },
};
const REVIEW_NUMBER_WIDTH = "150px";
const REVIEW_UNIT_WIDTH = "64px";
const REVIEW_SIGN_WIDTH = "52px";
const REVIEW_ACTION_WIDTH = "34px";

function ReviewNumber({ value, unit, color, weight, tip, strike = false }) {
  const number = (
    <Text
      textDecoration={strike ? "line-through" : undefined}
      w={REVIEW_NUMBER_WIDTH}
      flexShrink={0}
      textAlign="right"
      fontSize="14px"
      fontWeight={weight}
      color={color}
      sx={{ fontVariantNumeric: "tabular-nums" }}
      noOfLines={1}
    >
      {value}
    </Text>
  );
  return (
    <Flex align="baseline" flexShrink={0}>
      {tip ? (
        <Tooltip label={tip} hasArrow>
          {number}
        </Tooltip>
      ) : (
        number
      )}
      <Tooltip label={unit} isDisabled={!unit || unit.length < 8} hasArrow>
        <Text w={REVIEW_UNIT_WIDTH} flexShrink={0} pl={2} fontSize="12px" color={color} noOfLines={1}>
          {unit || ""}
        </Text>
      </Tooltip>
    </Flex>
  );
}

function ReviewValueCells({ value, unit, current, tone = "orange", onShowDetails, isIgnored, onToggleIgnored }) {
  const t = isIgnored ? CURRENT_TONE.ignored : CURRENT_TONE[tone];
  const currentValue = current
    ? current.valueNumeric != null
      ? getFormattedNumValue(current.valueNumeric, 2)
      : current.dataValue || "—"
    : null;
  return (
    // Colour on each Text: the theme gives Text its own colour, so one set on a Flex never reaches them.
    <Flex justify="flex-end" align="center" py="7px" opacity={isIgnored ? 0.6 : 1}>
      {current ? (
        <ReviewNumber
          value={currentValue}
          unit={splitPerUnit(current.unit).base}
          color={t.text}
          weight="600"
          tip={t.tip}
        />
      ) : (
        <Box w={`calc(${REVIEW_NUMBER_WIDTH} + ${REVIEW_UNIT_WIDTH})`} flexShrink={0} />
      )}
      <Flex w={REVIEW_SIGN_WIDTH} flexShrink={0} align="center" justify="center" gap={1}>
        {current ? (
          <>
            <IconButton
              size="xs"
              variant="ghost"
              minW="20px"
              h="20px"
              color={t.text}
              aria-label="Details of the current value"
              icon={<Info size={14} />}
              onClick={onShowDetails}
            />
            <Box color={t.sign}>
              {tone === "green" ? <Equals size={14} weight="bold" /> : <ArrowRight size={14} weight="bold" />}
            </Box>
          </>
        ) : null}
      </Flex>
      <ReviewNumber
        value={value}
        unit={unit}
        color={isIgnored ? "gray.400" : "gray.700"}
        weight="600"
        strike={isIgnored}
        tip={isIgnored ? "Ignored — this document's value will not be added" : undefined}
      />
      {/* Fixed slot on every review row so the figures stay aligned; the cross only where there is a current
          figure — that is what ignoring keeps. */}
      <Flex w={REVIEW_ACTION_WIDTH} flexShrink={0} justify="flex-end">
        {onToggleIgnored ? (
          <Tooltip
            label={
              isIgnored
                ? "Include this row again"
                : tone === "green"
                ? "Ignore this row for this ingest — keep the value and its source as they are (in development: not active yet)"
                : "Ignore this row for this ingest — keep the current value (in development: not active yet)"
            }
            hasArrow
          >
            <IconButton
              size="xs"
              variant="ghost"
              minW="22px"
              h="22px"
              color={isIgnored ? "gray.600" : "gray.400"}
              _hover={{ color: isIgnored ? "darkTeal" : "red.500", bg: "gray.100" }}
              aria-label={isIgnored ? "Include this row again" : "Ignore this row"}
              icon={isIgnored ? <ArrowCounterClockwise size={14} weight="bold" /> : <X size={14} weight="bold" />}
              onClick={onToggleIgnored}
            />
          </Tooltip>
        ) : null}
      </Flex>
    </Flex>
  );
}
